import React, { useState, useEffect } from 'react';
import { Form, Button, Collapse, message } from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import MedicalConditionsSection from './intake/MedicalConditionsSection';
import SurgicalHistorySection from './intake/SurgicalHistorySection';
import FamilyHistorySection from './intake/FamilyHistorySection';
import AllergiesSection from './intake/AllergiesSection';
import CurrentMedicationsSection from './intake/CurrentMedicationsSection';
import VaccinationHistorySection from './intake/VaccinationHistorySection';

const { Panel } = Collapse;

const PatientIntakeMedicalHistory = ({ sessionId, initialData, onSave, onNext, onPrevious }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [activeKey, setActiveKey] = useState(['medical-conditions']);
  const { t } = useTranslation();

  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      form.setFieldsValue(initialData);
    }
  }, [initialData, form]);

  const handleFinish = async (values) => {
    setLoading(true);
    
    // Clean up empty entries
    const cleanedValues = {
      medicalConditions: (values.medicalConditions || []).filter(item => 
        item && (item.condition || '').trim()
      ),
      surgicalHistory: (values.surgicalHistory || []).filter(item => 
        item && (item.procedure || '').trim()
      ),
      familyHistory: (values.familyHistory || []).filter(item => 
        item && (item.condition || '').trim() && (item.relation || '').trim()
      ),
      allergies: (values.allergies || []).filter(item => 
        item && (item.substance || '').trim()
      ),
      currentMedications: (values.currentMedications || []).filter(item => 
        item && (item.medicationName || '').trim()
      ),
      vaccinationHistory: (values.vaccinationHistory || []).filter(item => 
        item && (item.vaccineName || '').trim()
      )
    };

    const success = await onSave(cleanedValues);
    if (success) {
      // Scroll to top before moving to next step
      window.scrollTo({ top: 0, behavior: 'smooth' });
      onNext();
    }
    setLoading(false);
  };

  const handlePrevious = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    onPrevious();
  };

  return (
    <div className="intake-form-section">
      <h3>{t('intake.medicalHistory.title')}</h3>
      <p>{t('intake.medicalHistory.description')}</p>
      
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        autoComplete="off"
        size="large"
      >
        <Collapse 
          activeKey={activeKey} 
          onChange={setActiveKey}
          className="medical-history-collapse"
        >
          <Panel header={t('intake.medicalHistory.medicalConditions.title')} key="medical-conditions">
            <p>{t('intake.medicalHistory.medicalConditions.description')}</p>
            <MedicalConditionsSection />
          </Panel>

          <Panel header={t('intake.medicalHistory.surgicalHistory.title')} key="surgical-history">
            <p>{t('intake.medicalHistory.surgicalHistory.description')}</p>
            <SurgicalHistorySection />
          </Panel>

          <Panel header={t('intake.medicalHistory.familyHistory.title')} key="family-history">
            <p>{t('intake.medicalHistory.familyHistory.description')}</p>
            <FamilyHistorySection />
          </Panel>

          <Panel header={t('intake.medicalHistory.allergies.title')} key="allergies">
            <p>{t('intake.medicalHistory.allergies.description')}</p>
            <AllergiesSection />
          </Panel>

          <Panel header={t('intake.medicalHistory.currentMedications.title')} key="current-medications">
            <p>{t('intake.medicalHistory.currentMedications.description')}</p>
            <CurrentMedicationsSection />
          </Panel>

          <Panel header={t('intake.medicalHistory.vaccinationHistory.title')} key="vaccination-history">
            <p>{t('intake.medicalHistory.vaccinationHistory.description')}</p>
            <VaccinationHistorySection />
          </Panel>
        </Collapse>

        <div className="intake-form-buttons">
          <Button
            onClick={handlePrevious}
            size="large"
          >
            {t('intake.medicalHistory.previous')}
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            size="large"
          >
            {t('intake.medicalHistory.saveAndContinue')}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default PatientIntakeMedicalHistory;
